import { MouseEvent, useState } from "react"
import { InstructionsTableColumns } from "./InstructionsTableColumns"
import { TableConfiguration } from "components/ui/tables/TableConfiguration/TableConfiguration"
import { Editor } from "components/ui/editor/Editor"
import { CollapseBlueIcon } from "components/ui/icons/collapseIcon/CollapseBlueIcon"
import { CollapseGrayIcon } from "components/ui/icons/collapseIcon/CollapseGrayIcon"
import { useInstructionsStore } from "store/aiAgent.store"
import { EditInstruction } from "../EditInstruction/EditInstruction"
import { DeleteModal } from "components/ui/modals/DeleteModal/DeleteModal"
import { useDeleteInstruction } from "hooks/api/aiAgent/aiAgent.hook"
import { useMessage } from "hooks/message.hook"
import type { ExpandableConfig } from "antd/es/table/interface"
import type { IInstructionsTable } from "types/table.type"
import style from "./InstructionsTable.module.scss"

const { expandedRow } = style

type Props = { instructionsData: IInstructionsTable[]; refetchInstructions: () => void }

export const InstructionsTable = ({ instructionsData, refetchInstructions }: Props) => {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [instructionToDelete, setInstructionToDelete] = useState<number | null>(null)

	const { clearInstructions } = useInstructionsStore()
	const { mutate: deleteInstructionMutation } = useDeleteInstruction()
	const { success: successMessage, error: errorMessage } = useMessage()

	// Function to handle delete click from dropdown
	const handleDeleteClick = (id: number) => {
		setInstructionToDelete(id)
		setIsDeleteModalOpen(true)
	}

	// Logic for modal opening
	const setEditInstructionModalOpen = () => setIsModalOpen(true)

	// Pass columns to table
	const tableColumns = InstructionsTableColumns({
		setEditInstructionModalOpen,
		onDeleteClick: handleDeleteClick,
	})

	// Pass data for table
	const tableData = instructionsData?.map((item) => ({
		id: item.id,
		key: item.id,
		question: item.question,
		sections: item.sections,
		status: item.status,
		question_type: item.question_type,
		active: {
			status: item.status,
			questionId: item.id,
		},
		prompt: item.prompt,
		action: item,
	}))

	// Logic for modal closing
	const setEditInstructionModalClose = () => {
		setIsModalOpen(false)
		clearInstructions()
	}

	// Expandable items for table
	const expandalble: ExpandableConfig<IInstructionsTable> = {
		expandedRowRender: (record) => <Editor value={record.prompt} isReadOnly={true} />,
		expandRowByClick: true,
		expandedRowClassName: () => expandedRow,
		expandIcon: ({ expanded, onExpand, record }) =>
			expanded ? (
				<div data-testid="collapseIconOpened">
					<CollapseBlueIcon rotate={90} onClick={(e) => onExpand(record, e as MouseEvent<HTMLElement>)} />
				</div>
			) : (
				<CollapseGrayIcon onClick={(e) => onExpand(record, e as MouseEvent<HTMLElement>)} />
			),
	}

	// Handle row hover
	const onRowHandle = () => ({
		onMouseEnter: () => {
			document.body.style.cursor = "pointer"
		},
		onMouseLeave: () => {
			document.body.style.cursor = "default"
		},
	})

	const deleteInstructionHandle = () => {
		if (instructionToDelete) {
			deleteInstructionMutation(instructionToDelete, {
				onSuccess: () => {
					refetchInstructions()
					successMessage("Instruction deleted successfully")
					setIsDeleteModalOpen(false)
					setInstructionToDelete(null)
				},
				onError: () => {
					errorMessage("Failed to delete instruction")
				},
			})
		}
	}

	return (
		<>
			<TableConfiguration columns={tableColumns} data={tableData} expandable={expandalble} onRow={onRowHandle} />
			<EditInstruction
				isModalOpen={isModalOpen}
				setModalClose={setEditInstructionModalClose}
				refetchInstructions={refetchInstructions}
			/>
			<DeleteModal
				deleteHandle={deleteInstructionHandle}
				text="Are you sure you want to delete this instruction?"
				title="This action cannot be reverted."
				isPending={false}
				isModalOpen={isDeleteModalOpen}
				setModalClose={() => {
					setIsDeleteModalOpen(false)
					setInstructionToDelete(null)
				}}
			/>
		</>
	)
}
